// ════════════════════════════════════════════════════════════════════════
// resources.ts — Catálogo de documentos estáticos del panel (CVs, guías,
// tests psicológicos, ofertas). Los archivos viven en /public/resources/
// y Vite los sirve tal cual en esa misma ruta — ResourceViewerPage solo
// los busca aquí por slug y los muestra en un iframe.
// ════════════════════════════════════════════════════════════════════════

export type ResourceKind = 'pdf' | 'html' | 'gallery';

export interface Resource {
  slug: string;
  title: string;
  description: string;
  kind: ResourceKind;
  /** Para 'pdf' | 'html': ruta del archivo en /public. Para 'gallery': sin uso. */
  file?: string;
  /** Solo para 'gallery': lista de imágenes en /public. */
  images?: { file: string; caption: string }[];
}

export const CV_RESOURCES: Resource[] = [
  {
    slug: 'cv-tech-lead-manager-en',
    title: 'CV — Tech Lead Manager (EN)',
    description: 'Versión en inglés del CV enfocado en liderazgo técnico.',
    kind: 'html',
    file: '/resources/cv/cv-tech-lead-manager-en.html',
  },
  {
    slug: 'cv-tech-lead-manager-es',
    title: 'CV — Tech Lead Manager (ES)',
    description: 'Versión en español del CV enfocado en liderazgo técnico.',
    kind: 'html',
    file: '/resources/cv/cv-tech-lead-manager-es.html',
  },
  {
    slug: 'cv-fullstack-developer',
    title: 'CV — Full-Stack Developer',
    description: 'Perfil orientado a un rol de desarrollo Full-Stack.',
    kind: 'html',
    file: '/resources/cv/cv-fullstack-developer.html',
  },
  {
    slug: 'cv-senior-developer-engineer-es',
    title: 'CV — Senior Developer Engineer (ES)',
    description: 'Perfil orientado a un rol de Senior Developer Engineer.',
    kind: 'html',
    file: '/resources/cv/cv-senior-developer-engineer-es.html',
  },
  {
    slug: 'cv-subdirector-it-cloud-es',
    title: 'CV — Subdirector IT y Cloud (ES)',
    description: 'Perfil orientado a un rol de dirección de IT y Cloud.',
    kind: 'html',
    file: '/resources/cv/cv-subdirector-it-cloud-es.html',
  },
];

export const BACKEND_RESOURCES: Resource[] = [
  {
    slug: 'terraform-associate-004',
    title: 'Terraform Associate (004)',
    description: 'Guía interactiva en español: 8 dominios, 37 objetivos, laboratorios y 32 preguntas de repaso con respuestas explicadas.',
    kind: 'html',
    file: '/resources/backend/guia-terraform.html',
  },
  {
    slug: 'comparativa-lenguajes-v1',
    title: 'Comparativa de Lenguajes — Microservicios & AWS',
    description: 'Comparativa de lenguajes backend para microservicios en AWS.',
    kind: 'pdf',
    file: '/resources/backend/comparativa-lenguajes-v1.pdf',
  },
  {
    slug: 'comparativa-lenguajes-v2',
    title: 'Comparativa de Lenguajes v2 — GraphQL & SignalR',
    description: 'Segunda versión de la comparativa, con foco en GraphQL y SignalR.',
    kind: 'pdf',
    file: '/resources/backend/comparativa-lenguajes-v2.pdf',
  },
  {
    slug: 'comparativa-lenguajes-v3',
    title: 'Comparativa de Lenguajes v3',
    description: 'Tercera versión de la comparativa de lenguajes backend.',
    kind: 'pdf',
    file: '/resources/backend/comparativa-lenguajes-v3.pdf',
  },
  {
    slug: 'comparativa-lenguajes-v4',
    title: 'Comparativa de Lenguajes v4',
    description: 'Cuarta versión de la comparativa de lenguajes backend.',
    kind: 'pdf',
    file: '/resources/backend/comparativa-lenguajes-v4.pdf',
  },
  {
    slug: 'design-principles',
    title: 'Design Principles — SOLID, KISS, DRY, YAGNI',
    description: 'Guía de principios de diseño de software.',
    kind: 'html',
    file: '/resources/backend/design-principles.html',
  },
  {
    slug: 'nodejs-typescript',
    title: 'Experiencia — Node.js & TypeScript',
    description: 'Resumen de experiencia con Node.js y TypeScript.',
    kind: 'pdf',
    file: '/resources/backend/nodejs-typescript.pdf',
  },
  {
    slug: 'graphql-crash-course',
    title: 'GraphQL Crash Course (2h)',
    description: 'Curso rápido de GraphQL.',
    kind: 'pdf',
    file: '/resources/backend/graphql-crash-course.pdf',
  },
  {
    slug: 'guia-mcp-graphql-msk',
    title: 'Guía — MCP, GraphQL & MSK',
    description: 'Guía técnica sobre MCP, GraphQL y Amazon MSK.',
    kind: 'pdf',
    file: '/resources/backend/guia-mcp-graphql-msk.pdf',
  },
  {
    slug: 'hubspot-crm',
    title: 'HubSpot CRM — Casos de Uso',
    description: 'Casos de uso de integración con HubSpot CRM.',
    kind: 'pdf',
    file: '/resources/backend/hubspot-crm.pdf',
  },
  {
    slug: 'patrones-diseno-csharp',
    title: 'Patrones de Diseño en C# (2026)',
    description: 'Guía de patrones de diseño aplicados en C#.',
    kind: 'pdf',
    file: '/resources/backend/patrones-diseno-csharp.pdf',
  },
  {
    slug: 'stack-tecnologico',
    title: 'Stack Tecnológico — Guía de Estudio',
    description: 'Guía de estudio del stack tecnológico completo.',
    kind: 'pdf',
    file: '/resources/backend/stack-tecnologico.pdf',
  },
  {
    slug: 'ingles-speech-jobs-report',
    title: 'Speech (Inglés) — Jobs Report',
    description: 'Transcripción de un discurso en inglés sobre el reporte de empleo.',
    kind: 'pdf',
    file: '/resources/backend/ingles-speech-jobs-report.pdf',
  },
];

export const PSICOLOGIA_RESOURCES: Resource[] = [
  {
    slug: 'integridad-laboral',
    title: 'Inventario de Integridad Laboral (EVT MX)',
    description: 'Resultado del inventario de integridad laboral Etegrity.',
    kind: 'pdf',
    file: '/resources/psicologia/integridad-laboral.pdf',
  },
  {
    slug: 'test-disc',
    title: 'Test de Comportamiento DISC',
    description: 'Resultado del test de comportamiento DISC.',
    kind: 'html',
    file: '/resources/psicologia/test-disc.html',
  },
  {
    slug: 'test-hpti',
    title: 'Test de Estilos de Trabajo (HPTI)',
    description: 'Resultado del test de estilos de trabajo HPTI.',
    kind: 'html',
    file: '/resources/psicologia/test-hpti.html',
  },
  {
    slug: 'capturas-tests',
    title: 'Capturas de tests',
    description: 'Galería de capturas de distintos tests de personalidad y evaluación.',
    kind: 'gallery',
    images: [
      { file: '/resources/psicologia/imagenes/evaluatest-01.jpg', caption: 'Evaluatest — 1' },
      { file: '/resources/psicologia/imagenes/evaluatest-02.jpg', caption: 'Evaluatest — 2' },
      { file: '/resources/psicologia/imagenes/evt-mx-disc-01.jpg', caption: 'EVT MX · DISC — 1' },
      { file: '/resources/psicologia/imagenes/optimizar-cv.jpeg', caption: 'Optimizar CV' },
      { file: '/resources/psicologia/imagenes/prueba1.jpg', caption: 'Prueba 1' },
      { file: '/resources/psicologia/imagenes/prueba-said1.jpg', caption: 'Prueba — Said' },
      { file: '/resources/psicologia/imagenes/prueba-vale1.jpg', caption: 'Prueba — Vale' },
      { file: '/resources/psicologia/imagenes/speak-assessment.jpg', caption: 'Speaking Assessment' },
      { file: '/resources/psicologia/imagenes/v1-test.jpg', caption: 'V1 Test' },
    ],
  },
];

export const OFERTAS_RESOURCES: Resource[] = [
  {
    slug: 'oferta-sr-fullstack-react-net',
    title: 'Sr. Full-Stack Developer (React + .NET)',
    description: 'Oferta de trabajo — Sr. Full Stack Developer, React + .NET.',
    kind: 'pdf',
    file: '/resources/ofertas/sr-fullstack-react-net.pdf',
  },
];

export const CERTIFICACIONES_RESOURCES: Resource[] = [
  {
    slug: 'guia-terraform-associate-004',
    title: 'Terraform Associate (004)',
    description: 'Guía de estudio: 8 dominios con ejemplos de código, ejercicios prácticos y 39 preguntas de repaso con explicación.',
    kind: 'html',
    file: '/resources/certificaciones/guia-terraform-associate-004.html',
  },
];

export const ALL_RESOURCES: Resource[] = [
  ...CV_RESOURCES,
  ...BACKEND_RESOURCES,
  ...PSICOLOGIA_RESOURCES,
  ...OFERTAS_RESOURCES,
  ...CERTIFICACIONES_RESOURCES,
];

export function findResource(slug: string | undefined): Resource | undefined {
  return ALL_RESOURCES.find((r) => r.slug === slug);
}
